// UserSearchBar — a debounced text-input that queries /api/users and shows a
// dropdown of matching profile chips. Tap any result to navigate to that
// user's profile (Follow / Friend / Message actions already wired there).
//
// Used in two places:
//   • Layout.jsx header  → compact `variant="header"` (icon-toggle popover)
//   • Profile.jsx page   → `variant="page"` (full-width bar under the header)
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "../lib/api";

export default function UserSearchBar({ variant = "page", placeholder = "Search people…", onResultClick }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setItems([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users?q=${encodeURIComponent(q.trim())}&limit=12`);
        setItems(Array.isArray(data) ? data : []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (u) => {
    setOpen(false); setFocused(false); setQ("");
    onResultClick?.(u);
    nav(`/u/${u.id}`);
  };

  // Header variant: toggle icon → floating input
  if (variant === "header") {
    return (
      <div ref={wrapRef} className="relative" data-testid="user-search-header">
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-black/70 hover:text-black transition"
          title="Search people"
          data-testid="user-search-toggle"
        >
          <Search className="w-4 h-4" />
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-72 sm:w-80 z-50 rounded-xl border border-black/10 bg-white shadow-xl p-2" data-testid="user-search-panel">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-black/10">
              <Search className="w-3.5 h-3.5 text-black/40" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Find people by name…"
                className="flex-1 bg-transparent text-sm text-nexus-obsidian placeholder-black/40 outline-none"
                data-testid="user-search-input-header"
              />
              {q && <button onClick={() => setQ("")} className="text-black/40 hover:text-black" data-testid="user-search-clear-header"><X className="w-3.5 h-3.5" /></button>}
            </div>
            <ResultsList items={items} loading={loading} q={q} onPick={go} />
          </div>
        )}
      </div>
    );
  }

  // Page variant: full-width bar
  return (
    <div ref={wrapRef} className="relative" data-testid="user-search-page">
      <div className={`flex items-center gap-2 px-3 py-2.5 rounded-full border transition-colors bg-white ${focused ? "border-nexus-cyan shadow-sm" : "border-black/10"}`}>
        <Search className="w-4 h-4 text-black/40" />
        <input
          value={q}
          onFocus={() => setFocused(true)}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-nexus-obsidian placeholder-black/40 outline-none"
          data-testid="user-search-input"
        />
        {q && <button onClick={() => setQ("")} className="text-black/40 hover:text-black" data-testid="user-search-clear"><X className="w-4 h-4" /></button>}
      </div>
      {focused && q && (
        <div className="absolute left-0 right-0 mt-2 z-40 rounded-xl border border-black/10 bg-white shadow-xl max-h-[60vh] overflow-y-auto" data-testid="user-search-results">
          <ResultsList items={items} loading={loading} q={q} onPick={go} />
        </div>
      )}
    </div>
  );
}

function ResultsList({ items, loading, q, onPick }) {
  if (loading) return <div className="p-3 text-xs text-black/50" data-testid="user-search-loading">Searching…</div>;
  if (!items.length) return <div className="p-3 text-xs text-black/50" data-testid="user-search-empty">No people match “{q}”.</div>;
  return (
    <ul className="py-1">
      {items.map((u) => (
        <li key={u.id}>
          <button
            onClick={() => onPick(u)}
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-black/5 transition text-left"
            data-testid={`user-search-result-${u.id}`}
          >
            <img src={u.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${u.id}`} alt="" className="w-9 h-9 rounded-full ring-1 ring-black/10 object-cover" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-nexus-obsidian font-medium truncate">{u.display_name}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-black/40">{u.role || "member"}</div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
